import { useEffect, useState } from 'react';
import { DollarSign, AlertCircle, CheckCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

export default function AffiliatePayments() {
  const [periods, setPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payForm, setPayForm] = useState<any>({ amount: 0, method: 'check', reference: '', paid_at: new Date().toISOString().slice(0, 10), notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchPending = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/affiliates/payments/pending?include_suspended=${includeSuspended}`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: any[] };
    if (json.success) setPeriods(json.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSuspended]);

  const openPayForm = (p: any) => {
    setPayingId(p.id);
    setPayForm({
      amount: p.outstanding,
      method: p.affiliate.payment_method_preference || 'check',
      reference: '',
      paid_at: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    setError(null);
  };

  const submitPay = async () => {
    setError(null);
    const body: any = { period_id: payingId, ...payForm };
    if (body.method === 'wallet_credit') {
      delete body.amount; // wallet_credit pays full outstanding
      delete body.reference;
    }
    const res = await fetch(`${API}/admin/affiliates/payments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    if (!json.success) {
      setError(json.error || 'Payment failed');
      return;
    }
    setSuccess(`Payment recorded.`);
    setPayingId(null);
    setTimeout(() => setSuccess(null), 3000);
    fetchPending();
  };

  // Hide periods for affiliates with negative balance by default
  const visible = periods.filter((p) => p.running_balance >= 0);
  const heldByBalance = periods.filter((p) => p.running_balance < 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Pending Payments</h2>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={includeSuspended} onChange={(e) => setIncludeSuspended(e.target.checked)} />
          Include suspended affiliates
        </label>
      </div>

      {success && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{success}</div>}

      {heldByBalance.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2 text-red-800">
            <AlertCircle className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-medium">{heldByBalance.length} period(s) on HOLD — affiliate has negative running balance</div>
              <div className="text-sm mt-1">Future positive accruals must bring balance above $0 before these can be paid. Visible below in the held section.</div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading...</div>
      ) : (
        <>
          <PaymentsTable
            title="Ready to Pay"
            periods={visible}
            onPay={openPayForm}
            payingId={payingId}
            payForm={payForm}
            setPayForm={setPayForm}
            submitPay={submitPay}
            cancelPay={() => setPayingId(null)}
            error={error}
          />

          {heldByBalance.length > 0 && (
            <div className="mt-6">
              <PaymentsTable
                title="On Hold (Negative Balance)"
                periods={heldByBalance}
                onPay={() => { /* disabled table — Pay buttons are not rendered */ }}
                payingId={null}
                payForm={null}
                setPayForm={() => {}}
                submitPay={() => {}}
                cancelPay={() => {}}
                error={null}
                disabled
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PaymentsTable({ title, periods, onPay, payingId, payForm, setPayForm, submitPay, cancelPay, error, disabled }: any) {
  if (periods.length === 0) return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm text-gray-500">Nothing to display.</div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b font-medium">{title} ({periods.length})</div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-3">Affiliate</th>
            <th className="text-left p-3">Period</th>
            <th className="text-left p-3">Method Pref</th>
            <th className="text-right p-3">Outstanding</th>
            <th className="text-right p-3">Running Balance</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p: any) => (
            <>
              <tr key={p.id} className="border-b">
                <td className="p-3">
                  <div className="font-medium">{p.affiliate.display_name}</div>
                  <div className="text-xs text-gray-500 font-mono">{p.affiliate.referral_code}</div>
                  {p.affiliate.status === 'suspended' && <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">suspended — accruing</span>}
                </td>
                <td className="p-3">{p.period_start}</td>
                <td className="p-3 text-xs">{p.affiliate.payment_method_preference}</td>
                <td className="p-3 text-right font-mono font-bold">${Number(p.outstanding).toFixed(2)}</td>
                <td className={`p-3 text-right font-mono ${p.running_balance < 0 ? 'text-red-600 font-bold' : ''}`}>
                  ${Number(p.running_balance).toFixed(2)}
                </td>
                <td className="p-3 text-right">
                  {!disabled && payingId !== p.id && (
                    <button
                      onClick={() => onPay(p)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      <DollarSign className="w-3 h-3" /> Pay
                    </button>
                  )}
                </td>
              </tr>
              {payingId === p.id && (
                <tr className="bg-gray-50">
                  <td colSpan={6} className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div>
                        <label className="text-xs text-gray-600">Method</label>
                        <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="w-full border rounded px-2 py-1 text-sm">
                          <option value="check">Check</option>
                          <option value="ach_manual">ACH (Manual)</option>
                          <option value="wallet_credit">Wallet Credit{p.affiliate.linked_location_id ? '' : ' (no linked location!)'}</option>
                          <option value="stripe_connect">Stripe Connect</option>
                        </select>
                      </div>
                      {payForm.method !== 'wallet_credit' && (
                        <>
                          <div>
                            <label className="text-xs text-gray-600">Amount</label>
                            <input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: parseFloat(e.target.value) })} className="w-full border rounded px-2 py-1 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Reference (check # / ACH ID)</label>
                            <input type="text" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Paid At</label>
                            <input type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                          </div>
                        </>
                      )}
                      {payForm.method === 'wallet_credit' && (
                        <div className="md:col-span-3 text-sm text-gray-700">
                          Will credit ${Number(p.outstanding).toFixed(2)} to linked location's wallet.
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button onClick={cancelPay} className="px-3 py-1 text-gray-700 text-sm">Cancel</button>
                        <button onClick={submitPay} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Confirm</button>
                      </div>
                    </div>
                    {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}
